import "@/lib/sentry";
import * as Sentry from "@sentry/react";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@/components/ReactQueryDevtools";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RouteErrorPage } from "@/components/RouteErrorPage";
import { ToastProvider } from "@/components/ui/ToastProvider";
import { queryClient } from "@/lib/queryClient";
import "@fontsource/titillium-web/400.css";
import "@fontsource/titillium-web/600.css";
import "@fontsource/titillium-web/700.css";
import "./index.css";

// Pages are lazy-loaded so each route ships as its own chunk.
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    // Pathless root so a crash anywhere — including the standalone auth
    // pages below — has somewhere to bubble to instead of React Router's
    // default "Unexpected Application Error" screen.
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: "/login",
        lazy: async () => ({
          Component: (await import("@/pages/auth/LoginPage")).default,
        }),
      },
      {
        path: "/forgot-password",
        lazy: async () => ({
          Component: (await import("@/pages/auth/ForgotPasswordPage")).default,
        }),
      },
      {
        path: "/reset-password",
        lazy: async () => ({
          Component: (await import("@/pages/auth/ResetPasswordPage")).default,
        }),
      },
      {
        path: "/sign-up",
        lazy: async () => ({
          Component: (await import("@/pages/auth/SignUpPage")).default,
        }),
      },
      {
        path: "/activate",
        lazy: async () => ({
          Component: (await import("@/pages/auth/ActivatePage")).default,
        }),
      },
      {
        path: "/__previewsp",
        lazy: async () => ({
          Component: (await import("@/pages/__PreviewSP")).default,
        }),
      },
      {
        path: "/",
        element: <RequireAuth />,
        children: [
          {
            path: "change-password",
            lazy: async () => ({
              Component: (await import("@/pages/auth/ChangePasswordPage")).default,
            }),
          },
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              {
                path: "__previewsp-shell",
                lazy: async () => ({
                  Component: (await import("@/pages/__PreviewSP")).default,
                }),
              },
              {
                path: "__previewsp-v1",
                lazy: async () => ({
                  Component: (await import("@/pages/__PreviewSP")).Variant1,
                }),
              },
              {
                path: "__previewsp-v2",
                lazy: async () => ({
                  Component: (await import("@/pages/__PreviewSP")).Variant2,
                }),
              },
              {
                path: "__previewsp-v3",
                lazy: async () => ({
                  Component: (await import("@/pages/__PreviewSP")).Variant3,
                }),
              },
              {
                path: "__previewsp-v4",
                lazy: async () => ({
                  Component: (await import("@/pages/__PreviewSP")).Variant4,
                }),
              },
              {
                path: "dashboard",
                lazy: async () => ({
                  Component: (await import("@/pages/dashboard/DashboardPage"))
                    .default,
                }),
              },
              {
                path: "customers",
                lazy: async () => ({
                  Component: (await import("@/pages/customers/CustomersPage"))
                    .default,
                }),
              },
              {
                path: "customers/:id",
                lazy: async () => ({
                  Component: (await import("@/pages/customers/CustomerProfilePage"))
                    .default,
                }),
              },
              {
                path: "businesses",
                lazy: async () => ({
                  Component: (await import("@/pages/businesses/BusinessesPage"))
                    .default,
                }),
              },
              {
                path: "businesses/:id",
                lazy: async () => ({
                  Component: (await import("@/pages/businesses/BusinessProfilePage")).default,
                }),
              },
              {
                path: "bank-statements",
                lazy: async () => ({
                  Component: (await import("@/pages/statements/BankStatementsPage"))
                    .default,
                }),
              },
              {
                path: "mpesa-statements",
                lazy: async () => ({
                  Component: (
                    await import("@/pages/statements/MpesaStatementsPage")
                  ).default,
                }),
              },
              {
                path: "telco-statements",
                lazy: async () => ({
                  Component: (
                    await import("@/pages/statements/TelcoStatementsPage")
                  ).default,
                }),
              },
              {
                path: "merchant-transactions",
                lazy: async () => ({
                  Component: (
                    await import("@/pages/statements/TransactionsPage")
                  ).default,
                }),
              },
              {
                path: "batch-checks",
                lazy: async () => ({
                  Component: (await import("@/pages/checks/BatchChecksPage"))
                    .default,
                }),
              },
              {
                path: "crb-products",
                lazy: async () => ({
                  Component: (await import("@/pages/scoring/CrbProductsPage"))
                    .default,
                }),
              },
              {
                path: "crb-checks",
                element: <Navigate to="/batch-checks" replace />,
              },
              {
                path: "iprs-checks",
                element: <Navigate to="/batch-checks" replace />,
              },
              {
                path: "brs-checks",
                lazy: async () => ({
                  Component: (await import("@/pages/checks/BrsChecksPage")).default,
                }),
              },
              {
                path: "users",
                lazy: async () => ({
                  Component: (await import("@/pages/admin/UsersPage")).default,
                }),
              },
              {
                path: "billing",
                lazy: async () => ({
                  Component: (await import("@/pages/finance/BillingPage")).default,
                }),
              },
              {
                path: "topup",
                lazy: async () => ({
                  Component: (await import("@/pages/finance/TopupPage")).default,
                }),
              },
              {
                path: "usage-report",
                lazy: async () => ({
                  Component: (await import("@/pages/reports/UsageReportPage"))
                    .default,
                }),
              },
              {
                path: "developer",
                lazy: async () => ({
                  Component: (await import("@/pages/settings/DeveloperPage"))
                    .default,
                }),
              },
              {
                path: "profile",
                lazy: async () => ({
                  Component: (await import("@/pages/settings/ProfilePage")).default,
                }),
              },
              {
                path: "partners",
                lazy: async () => ({
                  Component: (await import("@/pages/admin/PartnersPage")).default,
                }),
              },
              {
                path: "roles",
                lazy: async () => ({
                  Component: (await import("@/pages/admin/RolesPage")).default,
                }),
              },
              {
                path: "audit-logs",
                lazy: async () => ({
                  Component: (await import("@/pages/admin/AuditLogsPage")).default,
                }),
              },
              {
                path: "platform-users",
                lazy: async () => ({
                  Component: (await import("@/pages/admin/PlatformUsersPage"))
                    .default,
                }),
              },
              {
                path: "partner-reports",
                lazy: async () => ({
                  Component: (await import("@/pages/reports/PartnerReportsPage"))
                    .default,
                }),
              },
              {
                path: "service-providers",
                lazy: async () => ({
                  Component: (await import("@/pages/finance/ServiceProvidersPage"))
                    .default,
                }),
              },
              {
                path: "iprs-wallet",
                lazy: async () => ({
                  Component: (await import("@/pages/wallet/IprsWalletPage"))
                    .default,
                }),
              },
              {
                path: "scoring-configs",
                lazy: async () => ({
                  Component: (await import("@/pages/scoring/ScoringConfigsPage"))
                    .default,
                }),
              },
              {
                path: "request-logs",
                lazy: async () => ({
                  Component: (await import("@/pages/support/RequestLogsPage"))
                    .default,
                }),
              },
              {
                path: "iprs-requests",
                lazy: async () => ({
                  Component: (await import("@/pages/support/IprsRequestsPage"))
                    .default,
                }),
              },
              {
                path: "request-stats",
                lazy: async () => ({
                  Component: (await import("@/pages/support/RequestStatsPage"))
                    .default,
                }),
              },
              {
                path: "stanbic-report",
                lazy: async () => ({
                  Component: (await import("@/pages/support/StanbicReportPage"))
                    .default,
                }),
              },
              {
                path: "pbp/members",
                lazy: async () => ({
                  Component: (await import("@/pages/support/PbpOnboardingPage/MembersPage"))
                    .default,
                }),
              },
              {
                path: "pbp/uploads",
                lazy: async () => ({
                  Component: (await import("@/pages/support/PbpOnboardingPage/UploadsPage"))
                    .default,
                }),
              },
              { path: "pbp", element: <Navigate to="/pbp/members" replace /> },
              {
                path: "pbp-onboarding",
                lazy: async () => ({
                  Component: (await import("@/pages/support/PbpOnboardingPage/LegacyRedirect"))
                    .default,
                }),
              },
              {
                path: "notifications",
                lazy: async () => ({
                  Component: (await import("@/pages/support/NotificationsPage"))
                    .default,
                }),
              },
              { path: "*", element: <Navigate to="/dashboard" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
        <Suspense fallback={null}><ReactQueryDevtools initialIsOpen={false} /></Suspense>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
